(() => {
  const configElement = document.getElementById('PVConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const locale = config.language || document.documentElement.lang || 'fr';
  let cartData = null;
  let cartPending = 0;
  let cartUncertain = false;
  let cartQueue = Promise.resolve();
  let cartFocus = null;
  const wiredDialogs = new WeakSet();
  const galleryCleanups = new WeakMap();
  const productCleanups = new WeakMap();
  const money = amount => new Intl.NumberFormat(locale, {
    style: 'currency', currency: config.currency || 'EUR'
  }).format(amount / 100);
  const esc = value => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);
  const endpoint = name => root + name + '.js';
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const locked = () => document.documentElement.classList.toggle('scroll-lock', !!document.querySelector('dialog[open]'));
  const drawer = () => document.getElementById('PVCart');

  function announceCart(message) {
    const status = drawer()?.querySelector('[data-cart-status]');
    if (status) status.textContent = message;
  }

  function cartError(message = '') {
    const error = drawer()?.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function toast(message) {
    const element = document.querySelector('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(element.timer);
    element.timer = setTimeout(() => element.classList.remove('is-visible'), 3500);
  }

  function dialogTriggerState(dialog, expanded) {
    if (!dialog.id) return;
    document.querySelectorAll('[aria-controls]').forEach(trigger => {
      if (trigger.getAttribute('aria-controls') === dialog.id && trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', String(expanded));
      }
    });
  }

  function openDialog(id, opener = document.activeElement) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    wireDialog(dialog);
    dialog.pvOpener = opener;
    dialog.showModal();
    dialogTriggerState(dialog, true);
    locked();
    return true;
  }

  function wireDialog(dialog) {
    if (wiredDialogs.has(dialog) || dialog.closest('poiliva-reviews')) return;
    wiredDialogs.add(dialog);
    dialog.addEventListener('close', () => {
      dialogTriggerState(dialog, false);
      locked();
      const opener = dialog.pvOpener;
      if (opener?.isConnected && !opener.disabled) opener.focus({preventScroll: true});
      dialog.pvOpener = null;
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
        dialog.close();
      }
    });
  }

  async function fetchJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const {headers, ...rest} = options;
    try {
      const response = await fetch(url, {
        ...rest,
        headers: {Accept: 'application/json', ...(headers || {})},
        signal: controller.signal
      });
      let data;
      try { data = await response.json(); }
      catch { throw new Error(config.error); }
      if (!response.ok) throw new Error(data.description || data.message || config.error);
      return data;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error(config.error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  function focusDescriptor(element) {
    if (!element || !drawer()?.contains(element)) return null;
    if (element.hasAttribute('data-cart-input')) return {key: element.dataset.cartInput, type: 'input'};
    if (element.hasAttribute('data-cart-key')) {
      return {key: element.dataset.cartKey, type: element.classList.contains('cart-remove') ? 'remove' : element.textContent.trim()};
    }
    return null;
  }

  function restoreCartFocus() {
    if (!cartFocus || !drawer()?.open) { cartFocus = null; return; }
    const current = document.activeElement;
    if (current !== document.body && current !== drawer() && current?.isConnected && !current.disabled) {
      cartFocus = null;
      return;
    }
    const candidates = [...drawer().querySelectorAll('[data-cart-input], [data-cart-key]')];
    const target = candidates.find(element => {
      const descriptor = focusDescriptor(element);
      return descriptor?.key === cartFocus.key && descriptor.type === cartFocus.type;
    }) || drawer().querySelector('[data-cart-input], [data-close]');
    target?.focus({preventScroll: true});
    cartFocus = null;
  }

  function syncCartControls() {
    const cart = drawer();
    if (!cart) return;
    const busy = cartPending > 0;
    const form = cart.querySelector('#PVDrawerCart');
    if (form) form.setAttribute('aria-busy', String(busy));
    cart.querySelectorAll('[data-cart-key], [data-cart-input]').forEach(element => { element.disabled = busy; });
    const checkout = cart.querySelector('[data-cart-checkout]');
    if (checkout) checkout.disabled = busy || cartUncertain || cartData?.item_count === 0;
  }

  function runCartAction(action) {
    if (cartPending === 0) cartFocus = focusDescriptor(document.activeElement);
    cartPending += 1;
    syncCartControls();
    announceCart(config.cartLoading || config.loading);
    const pending = cartQueue.then(action);
    cartQueue = pending.catch(() => {});
    return pending.finally(() => {
      cartPending -= 1;
      syncCartControls();
      if (cartPending === 0) restoreCartFocus();
    });
  }

  function renderCart(cart) {
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count) || !Number.isFinite(cart.total_price)) {
      throw new Error(config.cartError || config.error);
    }
    cartData = cart;
    document.querySelectorAll('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-total]').forEach(element => { element.textContent = money(cart.total_price); });
    const content = drawer()?.querySelector('[data-cart-items]');
    if (content) content.innerHTML = cart.items.map(item => {
      const title = esc(item.product_title);
      const key = esc(item.key);
      const quantity = Number(item.quantity);
      return '<article class="cart-item"><a href="' + esc(item.url) + '">' +
        (item.image ? '<img src="' + esc(item.image) + '" alt="' + title + '" width="82" height="82" loading="lazy">' : '') +
        '</a><div><h3><a href="' + esc(item.url) + '">' + title + '</a></h3>' +
        (item.variant_title && item.variant_title !== 'Default Title' ? '<div class="variant-title">' + esc(item.variant_title) + '</div>' : '') +
        '<p class="cart-item-price">' + money(item.final_line_price) + '</p><div class="quantity-control">' +
        '<button type="button" data-cart-key="' + key + '" data-cart-quantity="' + (quantity - 1) + '" aria-label="' + esc(config.decrease || config.quantity) + ' ' + title + '">−</button>' +
        '<input type="number" min="0" step="1" inputmode="numeric" required name="updates[]" value="' + quantity + '" data-cart-input="' + key + '" data-cart-saved="' + quantity + '" aria-label="' + esc(config.quantity) + ' ' + title + '">' +
        '<button type="button" data-cart-key="' + key + '" data-cart-quantity="' + (quantity + 1) + '" aria-label="' + esc(config.increase || config.quantity) + ' ' + title + '">+</button></div></div>' +
        '<button class="cart-remove" type="button" data-cart-key="' + key + '" data-cart-quantity="0" aria-label="' + esc(config.remove) + ' ' + title + '">' + esc(config.remove) + '</button></article>';
    }).join('');
    if (content) content.hidden = cart.item_count === 0;
    const empty = drawer()?.querySelector('[data-cart-empty]');
    const bottom = drawer()?.querySelector('[data-cart-bottom]');
    if (empty) empty.hidden = cart.item_count > 0;
    if (bottom) bottom.hidden = cart.item_count === 0;
    syncCartControls();
    document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart}}));
  }

  async function refreshCart() {
    const cart = await fetchJSON(endpoint('cart'), {cache: 'no-store'});
    renderCart(cart);
    cartUncertain = false;
    return cart;
  }

  async function loadCart() {
    return runCartAction(async () => {
      try {
        const cart = await refreshCart();
        cartError();
        announceCart(config.cartUpdated || config.cartTitle);
        return cart;
      } catch (error) {
        cartUncertain = true;
        cartError(config.cartError || error.message);
        announceCart('');
      }
    });
  }

  async function changeCart(key, quantity) {
    if (!Number.isInteger(quantity) || quantity < 0) {
      cartError(config.invalidQuantity || config.error);
      return;
    }
    return runCartAction(async () => {
      cartError();
      try {
        const cart = await fetchJSON(endpoint('cart/change'), {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({id: key, quantity})
        });
        renderCart(cart);
        cartUncertain = false;
        announceCart(config.cartUpdated || config.cartTitle);
      } catch (error) {
        cartUncertain = true;
        try { await refreshCart(); }
        catch { cartUncertain = true; }
        cartError(error.message || config.cartError || config.error);
        announceCart('');
      }
    });
  }

  function openZoom(button) {
    const image = button.querySelector('img');
    if (!image) return;
    let dialog = document.getElementById('PVZoom');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'PVZoom';
      dialog.className = 'pv-zoom';
      dialog.setAttribute('aria-labelledby', 'PVZoomTitle');
      const heading = document.createElement('h2');
      heading.id = 'PVZoomTitle';
      heading.className = 'sr-only';
      heading.textContent = config.imageZoom;
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'icon-button';
      close.dataset.close = '';
      close.autofocus = true;
      close.setAttribute('aria-label', config.close);
      close.textContent = '×';
      const fullImage = document.createElement('img');
      dialog.append(heading, close, fullImage);
      document.body.append(dialog);
    }
    const fullImage = dialog.querySelector('img');
    fullImage.src = image.currentSrc || image.src;
    fullImage.alt = image.alt;
    openDialog('PVZoom', button);
  }

  function setPrice(container, amount) {
    if (!container) return;
    const value = container.querySelector('[data-price-amount]');
    if (value) value.textContent = money(amount);
    else container.textContent = money(amount);
  }

  function updateVariant(section, select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    const available = option.dataset.available === 'true';
    const price = Number(option.dataset.price);
    const comparePrice = Number(option.dataset.comparePrice) || 0;
    setPrice(section.querySelector('[data-product-price]'), price);
    setPrice(section.querySelector('[data-sticky-price]'), price);
    const compare = section.querySelector('[data-compare-price]');
    if (compare) { compare.hidden = comparePrice <= price; setPrice(compare, comparePrice); }
    const unit = section.querySelector('[data-unit-price]');
    const count = Number(option.dataset.unitCount) || 0;
    if (unit) {
      unit.hidden = count <= 0;
      const amount = unit.querySelector('[data-unit-price-amount]');
      if (amount && count > 0) amount.textContent = money(price / count);
    }
    const contents = section.querySelector('[data-pack-contents]');
    if (contents && option.dataset.packContents) contents.textContent = option.dataset.packContents;
    const submit = section.querySelector('[data-product-submit]');
    if (submit) { submit.disabled = !available; submit.textContent = available ? config.add : config.soldOut; }
    const sticky = section.querySelector('[data-sticky-submit]');
    if (sticky) sticky.disabled = !available;
    const url = new URL(location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState({}, '', url);
    const variants = section.querySelector('[data-variant-json]');
    const variant = variants && JSON.parse(variants.textContent).find(item => String(item.id) === select.value);
    if (variant?.featured_media) {
      const media = [...section.querySelectorAll('[data-media-id]')].find(item => item.dataset.mediaId === String(variant.featured_media.id));
      media?.scrollIntoView({behavior: reducedMotion() ? 'instant' : 'smooth', block: 'nearest', inline: 'start'});
    }
  }

  document.addEventListener('click', async event => {
    const close = event.target.closest('[data-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const opener = event.target.closest('[data-open]');
    if (opener) { openDialog(opener.dataset.open, opener); return; }
    const cart = event.target.closest('[data-cart-open]');
    if (cart) {
      if (!drawer() || typeof drawer().showModal !== 'function') return;
      event.preventDefault();
      openDialog('PVCart', cart);
      await loadCart();
      return;
    }
    const quantity = event.target.closest('[data-quantity-step]');
    if (quantity) {
      const input = quantity.parentElement.querySelector('input');
      if (!input) return;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step) || 1;
      input.value = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(quantity.dataset.quantityStep) * step));
      input.dispatchEvent(new Event('change', {bubbles: true}));
      return;
    }
    const change = event.target.closest('[data-cart-key]');
    if (change && !change.disabled) {
      await changeCart(change.dataset.cartKey, Number(change.dataset.cartQuantity));
      return;
    }
    const zoom = event.target.closest('[data-product-zoom]');
    if (zoom) openZoom(zoom);
  });

  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-input]');
    if (input) {
      if (!input.checkValidity() || input.value.trim() === '') {
        input.setCustomValidity(config.invalidQuantity || config.error);
        input.reportValidity();
        cartError(config.invalidQuantity || config.error);
        return;
      }
      if (input.value !== input.dataset.cartSaved) changeCart(input.dataset.cartInput, Number(input.value));
      return;
    }
    const sort = event.target.closest('[data-sort]');
    if (sort) { sort.form.requestSubmit(); return; }
    const select = event.target.closest('[data-variant-select]');
    if (select) updateVariant(select.closest('[data-product-section]'), select);
  });

  document.addEventListener('input', event => {
    if (event.target.matches('[data-cart-input]')) event.target.setCustomValidity('');
    if (event.target.matches('[data-before-after]')) {
      event.target.closest('.ba-frame').style.setProperty('--position', event.target.value + '%');
    }
  });

  document.addEventListener('submit', async event => {
    const nativeCart = event.target.closest('#PVDrawerCart, #PVMainCart');
    if (nativeCart) {
      if (cartPending > 0 || (nativeCart.id === 'PVDrawerCart' && cartUncertain)) {
        event.preventDefault();
        if (nativeCart.id === 'PVDrawerCart') cartError(cartPending > 0 ? config.cartPending : config.cartError);
        else toast(config.cartPending || config.loading);
      }
      return;
    }
    const form = event.target.closest('.pv-add-form');
    if (!form) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    const payload = new FormData(form);
    const fields = [...form.querySelectorAll('input, select')].map(field => ({field, disabled: field.disabled}));
    form.setAttribute('aria-busy', 'true');
    fields.forEach(({field}) => { field.disabled = true; });
    const buttons = [...form.querySelectorAll('[type=submit]')];
    document.querySelectorAll('[data-sticky-submit]').forEach(button => { if (button.form === form) buttons.push(button); });
    const initial = buttons.map(button => ({text: button.textContent, disabled: button.disabled}));
    buttons.forEach(button => { button.disabled = true; button.textContent = config.loading; });
    const error = form.querySelector('[data-form-error]');
    if (error) error.textContent = '';
    try {
      await runCartAction(async () => {
        await fetchJSON(endpoint('cart/add'), {method: 'POST', body: payload});
        openDialog('PVCart', event.submitter || buttons[0]);
        try {
          await refreshCart();
          cartError();
          announceCart(config.added);
        } catch (refreshError) {
          cartUncertain = true;
          cartError(config.cartError || refreshError.message);
        }
      });
    } catch (failure) {
      if (error) error.textContent = failure.message;
      else toast(failure.message);
    } finally {
      form.removeAttribute('aria-busy');
      fields.forEach(({field, disabled}) => { field.disabled = disabled; });
      buttons.forEach((button, index) => {
        const section = form.closest('[data-product-section]');
        const option = section?.querySelector('[data-variant-select]')?.selectedOptions[0];
        button.disabled = option ? option.dataset.available !== 'true' : initial[index].disabled;
        button.textContent = initial[index].text;
      });
    }
  });

  function init(scope = document) {
    scope.querySelectorAll('dialog').forEach(wireDialog);
    scope.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (galleryCleanups.has(gallery)) return;
      const media = gallery.closest('.product-media');
      const slides = [...gallery.children];
      if (!media || !slides.length) return;
      const controller = new AbortController();
      const signal = controller.signal;
      let index = 0;
      let frame;
      const setIndex = () => {
        if (!gallery.clientWidth) return;
        index = Math.max(0, Math.min(slides.length - 1, Math.round(gallery.scrollLeft / gallery.clientWidth)));
        const counter = media.querySelector('[data-gallery-counter]');
        if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
        media.querySelectorAll('[data-gallery-target]').forEach((target, itemIndex) => target.setAttribute('aria-current', String(itemIndex === index)));
        const previous = media.querySelector('[data-gallery-prev]');
        const next = media.querySelector('[data-gallery-next]');
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };
      const go = target => gallery.scrollTo({
        left: Math.max(0, Math.min(slides.length - 1, target)) * gallery.clientWidth,
        behavior: reducedMotion() ? 'instant' : 'smooth'
      });
      media.querySelectorAll('[data-gallery-target]').forEach(target => target.addEventListener('click', event => {
        event.preventDefault(); go(Number(target.dataset.galleryTarget));
      }, {signal}));
      media.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(index - 1), {signal});
      media.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(index + 1), {signal});
      gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(frame); frame = requestAnimationFrame(setIndex);
      }, {signal, passive: true});
      const observer = new ResizeObserver(setIndex);
      observer.observe(gallery);
      galleryCleanups.set(gallery, () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(frame); galleryCleanups.delete(gallery); });
      setIndex();
    });
    scope.querySelectorAll('[data-product-section]').forEach(section => {
      if (productCleanups.has(section)) return;
      const form = section.querySelector('.pv-product-form');
      const sticky = section.querySelector('[data-product-sticky]');
      if (!form || !sticky) return;
      const observer = new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, {threshold: 0});
      observer.observe(form);
      productCleanups.set(section, () => { observer.disconnect(); productCleanups.delete(section); });
    });
  }

  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-gallery]').forEach(gallery => galleryCleanups.get(gallery)?.());
    event.target.querySelectorAll('[data-product-section]').forEach(section => productCleanups.get(section)?.());
    event.target.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.nav-dropdown[open]').forEach(details => { if (!details.contains(event.target)) details.open = false; });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.nav-dropdown[open]').forEach(details => { details.open = false; });
  });
})();
